import { useCallback, useMemo, useState } from 'react';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { StructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { StructureSet } from '~/domain/patient/structureset';
import { RoiFilter } from './types';
import { TYPE_SORT_ORDER, INTERPRETED_TYPE_GROUPS } from './constants';

/**
 * Filter + selection state for the Copy Structures dialog.
 *
 *   selectedRois = (filteredRois \ disabledRoiNumbers) \ uncheckedRois
 */
export function useRoiFilterAndSelection(
    sourceStructureSet: StructureSetReadModel,
    activeImageTargetSs: StructureSet | null,
    overwriteExisting: boolean,
    initialSelectedRoiNumbers?: ReadonlySet<number>
) {
    const [filters, setFilters] = useState<RoiFilter>({
        visibleOnly: false,
        approvedOnly: false,
        interpretedTypes: new Set(),
        searchText: '',
    });

    // Source ROIs the caller did NOT pre-select start unchecked. An omitted
    // pre-selection yields the empty set (today's select-all behavior).
    const initialUncheckedRois = useMemo(() => {
        const unchecked = new Set<number>();
        if (!initialSelectedRoiNumbers) {
            return unchecked;
        }
        for (const roi of sourceStructureSet.roiArray) {
            if (!initialSelectedRoiNumbers.has(roi.roiNumber)) {
                unchecked.add(roi.roiNumber);
            }
        }
        return unchecked;
        // Pre-selection is captured once on mount; later prop identity changes
        // must not silently reset the user's checkbox edits.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Persists across filter / target changes; cleared only by clearFilters
    // or by re-checking the row.
    const [uncheckedRois, setUncheckedRois] = useState<Set<number>>(
        () => new Set(initialUncheckedRois)
    );

    const presentTypeGroups = useMemo(() => {
        const sourceTypes = new Set(
            sourceStructureSet.roiArray.map(
                (r) => r.interpretedType as RoiInterpretedType
            )
        );
        return INTERPRETED_TYPE_GROUPS.filter((group) =>
            group.types.some((t) => sourceTypes.has(t))
        );
    }, [sourceStructureSet.roiArray]);

    const targetRoiNames = useMemo(() => {
        if (!activeImageTargetSs) {
            return new Set<string>();
        }
        return new Set(
            Array.from(activeImageTargetSs.dicomStructureSet.rois.values()).map(
                (roi) => roi.name
            )
        );
    }, [activeImageTargetSs]);

    // ROIs that have name conflicts — always computed for visual warnings
    const conflictingRoiNumbers = useMemo(() => {
        const conflicting = new Set<number>();
        if (targetRoiNames.size === 0) {
            return conflicting;
        }
        for (const roi of sourceStructureSet.roiArray) {
            if (targetRoiNames.has(roi.name)) {
                conflicting.add(roi.roiNumber);
            }
        }
        return conflicting;
    }, [sourceStructureSet.roiArray, targetRoiNames]);

    // ROIs that are actually disabled (unselectable) — respects overwrite toggle
    const disabledRoiNumbers = useMemo(() => {
        if (overwriteExisting) {
            return new Set<number>();
        }
        return conflictingRoiNumbers;
    }, [overwriteExisting, conflictingRoiNumbers]);

    const filteredRois = useMemo(() => {
        return sourceStructureSet.roiArray
            .filter((roi) => {
                if (filters.visibleOnly && !roi.visible) {
                    return false;
                }
                if (
                    filters.approvedOnly &&
                    roi.approvalStatus !== DICOMApprovalStatus.APPROVED
                ) {
                    return false;
                }
                if (
                    filters.interpretedTypes.size > 0 &&
                    !filters.interpretedTypes.has(
                        roi.interpretedType as RoiInterpretedType
                    )
                ) {
                    return false;
                }
                if (
                    filters.searchText &&
                    !roi.name
                        .toLowerCase()
                        .includes(filters.searchText.toLowerCase())
                ) {
                    return false;
                }
                return true;
            })
            .sort((a, b) => {
                const orderA = TYPE_SORT_ORDER[a.interpretedType] ?? 99;
                const orderB = TYPE_SORT_ORDER[b.interpretedType] ?? 99;
                if (orderA !== orderB) {
                    return orderA - orderB;
                }
                return a.name.localeCompare(b.name);
            });
    }, [sourceStructureSet.roiArray, filters]);

    const filteredRoiNumbers = useMemo(
        () =>
            new Set(
                filteredRois
                    .filter((r) => !disabledRoiNumbers.has(r.roiNumber))
                    .map((r) => r.roiNumber)
            ),
        [filteredRois, disabledRoiNumbers]
    );

    const selectedRois = useMemo(() => {
        const result = new Set<number>();
        for (const n of filteredRoiNumbers) {
            if (!uncheckedRois.has(n)) {
                result.add(n);
            }
        }
        return result;
    }, [filteredRoiNumbers, uncheckedRois]);

    const toggleRoi = useCallback(
        (roiNumber: number) => {
            if (disabledRoiNumbers.has(roiNumber)) {
                return;
            }
            setUncheckedRois((prev) => {
                const next = new Set(prev);
                if (next.has(roiNumber)) {
                    next.delete(roiNumber);
                } else {
                    next.add(roiNumber);
                }
                return next;
            });
        },
        [disabledRoiNumbers]
    );

    const allFilteredSelected = useMemo(
        () =>
            filteredRoiNumbers.size > 0 &&
            [...filteredRoiNumbers].every((n) => selectedRois.has(n)),
        [filteredRoiNumbers, selectedRois]
    );

    const someFilteredSelected = useMemo(
        () => [...filteredRoiNumbers].some((n) => selectedRois.has(n)),
        [filteredRoiNumbers, selectedRois]
    );

    const toggleAllFiltered = useCallback(() => {
        setUncheckedRois((prev) => {
            const next = new Set(prev);
            if (allFilteredSelected) {
                // Deselect every currently-filtered eligible ROI.
                filteredRoiNumbers.forEach((n) => next.add(n));
            } else {
                // Re-select every currently-filtered eligible ROI.
                filteredRoiNumbers.forEach((n) => next.delete(n));
            }
            return next;
        });
    }, [allFilteredSelected, filteredRoiNumbers]);

    const toggleFilter = useCallback((key: 'visibleOnly' | 'approvedOnly') => {
        setFilters((prev) => ({ ...prev, [key]: !prev[key] }));
    }, []);

    const toggleTypeGroup = useCallback(
        (types: readonly RoiInterpretedType[]) => {
            setFilters((prev) => {
                const next = new Set(prev.interpretedTypes);
                const allActive = types.every((t) => next.has(t));
                if (allActive) {
                    types.forEach((t) => next.delete(t));
                } else {
                    types.forEach((t) => next.add(t));
                }
                return { ...prev, interpretedTypes: next };
            });
        },
        []
    );

    const isGroupActive = useCallback(
        (types: readonly RoiInterpretedType[]) =>
            types.every((t) => filters.interpretedTypes.has(t)),
        [filters.interpretedTypes]
    );

    const clearFilters = useCallback(() => {
        setFilters({
            visibleOnly: false,
            approvedOnly: false,
            interpretedTypes: new Set(),
            searchText: '',
        });
        // Restore the initial pre-selection, not select-all: clearing filters
        // must never silently widen the copy scope.
        setUncheckedRois(new Set(initialUncheckedRois));
    }, [initialUncheckedRois]);

    const updateSearchText = useCallback((searchText: string) => {
        setFilters((prev) => ({ ...prev, searchText }));
    }, []);

    const hasActiveFilters =
        filters.visibleOnly ||
        filters.approvedOnly ||
        filters.interpretedTypes.size > 0 ||
        filters.searchText.length > 0;

    return {
        selectedRois,
        filters,
        filteredRois,
        presentTypeGroups,
        conflictingRoiNumbers,
        disabledRoiNumbers,
        allFilteredSelected,
        someFilteredSelected,
        hasActiveFilters,
        toggleRoi,
        toggleAllFiltered,
        toggleFilter,
        toggleTypeGroup,
        isGroupActive,
        clearFilters,
        updateSearchText,
    };
}
